import Link from "next/link";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { StarIcon } from "@heroicons/react/24/solid";

import type { StudentReview } from "@/content/student-reviews";
import { GoogleGLogo } from "@/components/reviews/google-g-logo";

export function formatReviewDate(value?: string) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** Display-only title case for reviewer names (hyphens and apostrophes supported). */
export function formatReviewerDisplayName(name: string) {
  return name
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word
        .split("-")
        .map((hyphenPart) =>
          hyphenPart
            .split("'")
            .map((segment) =>
              segment.length === 0
                ? segment
                : segment.charAt(0).toUpperCase() + segment.slice(1)
            )
            .join("'")
        )
        .join("-")
    )
    .join(" ");
}

export function ReviewStars({ rating }: { rating?: number }) {
  if (!rating) {
    return null;
  }

  return (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, index) => (
        <StarIcon
          key={index}
          className={`h-4 w-4 ${
            index < rating ? "text-amber-400" : "text-stone-200"
          }`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

type ReviewFigureProps = {
  review: StudentReview;
  featured?: boolean;
  /** Override link target (defaults to review.link). */
  href?: string;
  /** Footer CTA label (defaults to "Read on Google"). */
  linkLabel?: string;
  /**
   * When true, the whole card is one link (no nested anchors).
   * Use for internal routes such as /reviews/.
   */
  cardAsLink?: boolean;
};

const LINE_CLAMP_CLASS = "line-clamp-6";

export function ReviewFigure({
  review,
  featured = false,
  href,
  linkLabel = "Read on Google",
  cardAsLink = false,
}: ReviewFigureProps) {
  const publishedLabel = formatReviewDate(review.publishedAt);
  const targetHref = href ?? review.link;
  const isExternal = /^https?:\/\//i.test(targetHref);
  const displayName = formatReviewerDisplayName(review.author.name);
  const showGoogleLogo = review.source === "google";
  const quoteRef = useRef<HTMLQuoteElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  const measureOverflow = useCallback(() => {
    const el = quoteRef.current;
    if (!el) {
      return;
    }

    if (expanded) {
      el.classList.add(LINE_CLAMP_CLASS);
      const overflows = el.scrollHeight > el.clientHeight + 1;
      el.classList.remove(LINE_CLAMP_CLASS);
      setCanExpand(overflows);
      if (!overflows) {
        setExpanded(false);
      }
      return;
    }

    setCanExpand(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    measureOverflow();

    const el = quoteRef.current;
    const onResize = () => measureOverflow();
    window.addEventListener("resize", onResize);

    let observer: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onResize);
      observer.observe(el);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
    };
  }, [measureOverflow, review.body, featured]);

  const cardClassName =
    "flex flex-col rounded-2xl bg-white shadow-lg ring-1 ring-stone-900/5 transition hover:-translate-y-1 hover:shadow-xl";

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded((current) => !current);
  };

  const showToggle = canExpand || expanded;

  const quote = (
    <div
      className={`flex flex-1 flex-col ${
        featured ? "p-8 sm:p-10" : "p-6"
      }`}
    >
      <blockquote
        ref={quoteRef}
        className={`whitespace-pre-line text-stone-900 ${
          featured
            ? "text-xl font-semibold leading-8 sm:text-2xl sm:leading-9"
            : "text-base leading-7"
        } ${expanded ? "" : LINE_CLAMP_CLASS}`}
      >
        “{review.body}”
      </blockquote>
      {showToggle ? (
        <button
          type="button"
          onClick={handleToggle}
          className="pointer-events-auto mt-3 self-start text-sm font-semibold text-brand-700 hover:text-brand-800"
          aria-expanded={expanded}
        >
          {expanded ? "View less" : "View more"}
        </button>
      ) : null}
    </div>
  );

  const nameEl = cardAsLink ? (
    <span className="font-medium text-brand-700">{displayName}</span>
  ) : isExternal ? (
    <a
      href={targetHref}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-brand-700 hover:text-brand-800"
    >
      {displayName}
    </a>
  ) : (
    <Link
      href={targetHref}
      className="font-medium text-brand-700 hover:text-brand-800"
    >
      {displayName}
    </Link>
  );

  const linkEl = cardAsLink ? (
    <span className="text-sm font-semibold text-brand-700">{linkLabel}</span>
  ) : isExternal ? (
    <a
      href={targetHref}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm font-semibold text-brand-700 hover:text-brand-800"
    >
      {linkLabel}
    </a>
  ) : (
    <Link
      href={targetHref}
      className="text-sm font-semibold text-brand-700 hover:text-brand-800"
    >
      {linkLabel}
    </Link>
  );

  const caption = (
    <figcaption
      className={`flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-t border-stone-900/10 ${
        featured ? "px-8 py-5 sm:px-10" : "px-6 py-4"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {showGoogleLogo ? (
          <GoogleGLogo className="h-6 w-6 shrink-0" />
        ) : null}
        <div className="min-w-0">
          {nameEl}
          <p className="mt-1 text-xs text-stone-500">
            {publishedLabel || "Cosmetic College student"}
          </p>
        </div>
      </div>
      <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
        <ReviewStars rating={review.rating} />
        {linkEl}
      </div>
    </figcaption>
  );

  if (cardAsLink) {
    return (
      <figure
        className={`${cardClassName} relative h-full min-h-min ${
          expanded ? "self-start w-full" : ""
        }`}
      >
        <Link
          href={targetHref}
          className="absolute inset-0 z-0 rounded-2xl no-underline"
          aria-label={`${linkLabel} from ${displayName}`}
        />
        <div className="pointer-events-none relative z-[1] flex flex-1 flex-col">
          {quote}
          {caption}
        </div>
      </figure>
    );
  }

  return (
    <figure
      className={`${cardClassName} h-full min-h-min ${
        expanded ? "self-start w-full" : ""
      }`}
    >
      {quote}
      {caption}
    </figure>
  );
}
